import { useCallback, useEffect, useMemo, useState } from 'react';
import { FAST_API_BASE_PEOPLE } from '../config';
import { createSkillsSnapshot, normalizeCompaniesForEditing, resolveBrandAssets } from '../utils/profileTransforms';

const parseBrandFromSearch = () => {
  if (typeof window === 'undefined') {
    return 'defaultBrand';
  }
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('brand') || 'defaultBrand';
};

export const usePersonProfile = (accessToken, initialPersonId) => {
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedPersonId, setResolvedPersonId] = useState(initialPersonId || null);
  const [snapshot, setSnapshot] = useState({ list: [], map: {} });
  const [initialCompanies, setInitialCompanies] = useState([]);
  const brand = useMemo(parseBrandFromSearch, []);
  const { logoSrc, backgroundColor } = useMemo(() => resolveBrandAssets(brand), [brand]);

  const fetchPerson = useCallback(async () => {
    if (!accessToken) {
      setError('User authentication token missing.');
      setLoading(false);
      return;
    }

    const useQueryPersonId = Boolean(initialPersonId);
    const endpoint = useQueryPersonId
      ? `${FAST_API_BASE_PEOPLE}/${initialPersonId}`
      : `${FAST_API_BASE_PEOPLE}/self`;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (!response.ok) {
        let message = 'Failed to fetch person data';
        try {
          const body = await response.json();
          if (typeof body?.detail === 'string') {
            message = body.detail;
          } else if (Array.isArray(body?.detail) && body.detail[0]?.msg) {
            message = body.detail[0].msg;
          }
        } catch (_) {
          // ignore
        }
        throw new Error(message);
      }

      const data = await response.json();
      const derivedPersonId = useQueryPersonId ? initialPersonId : data?.personid;
      setResolvedPersonId(derivedPersonId != null ? String(derivedPersonId) : null);
      setPerson(data);
      const skillSnapshot = createSkillsSnapshot(data?.skills);
      setSnapshot(skillSnapshot);
      setInitialCompanies(normalizeCompaniesForEditing(data?.companies));
    } catch (err) {
      setError(err.message || 'Failed to fetch person data');
      setPerson(null);
      setSnapshot({ list: [], map: {} });
      setInitialCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, initialPersonId]);

  useEffect(() => {
    fetchPerson();
  }, [fetchPerson]);

  const refreshPerson = useCallback(() => {
    fetchPerson();
  }, [fetchPerson]);

  return {
    person,
    loading,
    error,
    resolvedPersonId,
    logoSrc,
    backgroundColor,
    skillSnapshot: snapshot,
    initialCompanies,
    refreshPerson
  };
};

export default usePersonProfile;
